// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RewardsBreakdownCard from "@rc/App/Components/RewardsBreakdownCard/RewardsBreakdownCard";
import RewardsTypesCard from "@rc/App/Components/RewardsTypes/RewardsTypesCard";
import TotalRewardsStatisticsCard from "@rc/App/Components/TotalRewardsStatistics/TotalRewardsStatisticsCard";
import getLoyaltyRewardsData from "@rc/App/Handlers/LoyaltyRewards/getLoyaltyRewardsData";
import RewardOwner from "@rc/XDL/Icons/RewardOwner";
import RewardPurchases from "@rc/XDL/Icons/RewardPurchases";
import RewardRecommendations from "@rc/XDL/Icons/RewardRecommendations";
import React, { FC, useEffect, useMemo, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface RewardsModal {
    discountName: string;
    discountAmount: number;
}

export interface WidgetData {
    rewardsAvailable: RewardsModal[];
    rewardsAboutToExpire: RewardsModal[];
    totalEarnedThisYear: number;
    totalRedeemedThisYear: number;
    rewardsFromPurchases: number;
    rewardsFromPetOwnerPurchases: number;
    rewardsFromRecommendations: number;
}

const enum fields {
    mainTitle = "mainTitle",
    subTitle = "subTitle",
    modalType = "modalType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.mainTitle]: string;
        [fields.subTitle]: string;
        [fields.modalType]: "shelter" | "breeder";
    };
}

const mapDispatchToProps = {
    getLoyaltyRewardsData,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

interface MyCrownRewardsStyles {
    rewardsContainer?: InjectableCss;
    mainTitle?: TypographyProps;
    breakdownContainer?: InjectableCss;
    subTitle?: TypographyProps;
    breakdownRewardCards?: InjectableCss;
    rewardsDivider?: InjectableCss;
    centeringWrapper?: InjectableCss;
}

export const myCrownRewardsStyles: MyCrownRewardsStyles = {};

const styles = myCrownRewardsStyles;

const StyledHr = getStyledWrapper("hr");

const MyCrownRewards: FC<Props> = ({ fields, getLoyaltyRewardsData }) => {
    const { mainTitle, subTitle, modalType } = fields;
    const [widgetData, setWidgetData] = useState<WidgetData>();
    const [isLoading, setIsLoading] = useState(true);

    const toaster = useToaster();

    const getCurrentYear = useMemo(() => {
        const now = new Date();
        return now.getFullYear();
    }, []);

    useEffect(() => {
        getLoyaltyRewardsData({
            modalType,
            onSuccess: (res: WidgetData) => {
                setWidgetData(res);
            },
            onError: (error: any) => {
                toaster.addToast({
                    body: <Typography>{siteMessage("Custom_Rewards_Api_Error_Message")}</Typography>,
                    messageType: "danger",
                });
            },
            onComplete: () => {
                setIsLoading(false);
            },
        });
    }, []);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper>
            <Typography {...styles.mainTitle}>{mainTitle}</Typography>
            <StyledWrapper {...styles.rewardsContainer}>
                <RewardsTypesCard
                    title={siteMessage("Custom_Rewards_Available")}
                    rewardTypes={widgetData?.rewardsAvailable}
                    rewardNoteMessage={siteMessage("Custom_Rewards_Available_Note")}
                />
                <RewardsTypesCard
                    title={siteMessage("Custom_Rewards_About_To_Expire")}
                    rewardTypes={widgetData?.rewardsAboutToExpire}
                    rewardNoteMessage={siteMessage("Custom_Rewards_About_To_Expire_Note")}
                />
                <TotalRewardsStatisticsCard
                    title={siteMessage("Custom_Rewards_Total_Earned_This_Year")}
                    value={widgetData?.totalEarnedThisYear}
                />
                <TotalRewardsStatisticsCard
                    title={siteMessage("Custom_Rewards_Total_Redeemed_This_Year")}
                    value={widgetData?.totalRedeemedThisYear}
                />
            </StyledWrapper>
            <StyledHr {...styles.rewardsDivider} />
            <StyledWrapper {...styles.breakdownContainer}>
                <Typography {...styles.subTitle}>
                    {getCurrentYear} {subTitle}
                </Typography>
                <StyledWrapper {...styles.breakdownRewardCards}>
                    <RewardsBreakdownCard
                        title={siteMessage("Custom_Rewards_From_Purchases")}
                        value={widgetData?.rewardsFromPurchases}
                        cardSrc={() => <RewardPurchases />}
                    />
                    {widgetData?.rewardsFromRecommendations !== undefined && (
                        <RewardsBreakdownCard
                            title={siteMessage("Custom_Rewards_From_Recommendations")}
                            value={widgetData?.rewardsFromRecommendations}
                            cardSrc={() => <RewardRecommendations />}
                        />
                    )}
                    {/* we need to remove this check later once we have data */}
                    {modalType === "breeder" && (
                        <RewardsBreakdownCard
                            title={siteMessage("Custom_Rewards_From_Pet_Owner_Purchases")}
                            value={widgetData?.rewardsFromPetOwnerPurchases}
                            cardSrc={() => <RewardOwner />}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(MyCrownRewards),
    definition: {
        displayName: "My Crown Rewards",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.mainTitle,
                displayName: "Widget Title",
                editorTemplate: "TextField",
                defaultValue: "My Crown Rewards",
                fieldType: "General",
            },
            {
                name: fields.subTitle,
                displayName: "Widget Sub Title",
                editorTemplate: "TextField",
                defaultValue: "Reward Breakdowns",
                fieldType: "General",
            },
            {
                name: fields.modalType,
                displayName: "Modal Type",
                editorTemplate: "DropDownField",
                defaultValue: "shelter",
                fieldType: "General",
                options: [
                    { displayName: "Shelter", value: "shelter" },
                    { displayName: "Breeder", value: "breeder" },
                ],
            },
        ],
    },
};

export default widgetModule;
